/** A real separable Gaussian, not widely spaced copies of the input image.
 * Large radii use a smaller surface: the kernel always samples neighbouring
 * pixels and costs at most 31 taps per axis. Static results are prepared once. */
export function frameBlurPlan(width:number,height:number,sigmaX:number,sigmaY=sigmaX) {
  const factor=Math.max(1,Math.max(sigmaX,sigmaY)/4,Math.max(width,height)/2048);
  const w=Math.max(1,Math.floor(width/factor)),h=Math.max(1,Math.floor(height/factor));
  return {width:w,height:h,sigmaX:Math.max(.001,sigmaX*w/width),sigmaY:Math.max(.001,sigmaY*h/height)};
}

let gpu:GaussianRenderer|null=null,unavailable=false;
export function disposeSmoothFrameBlur():void {gpu?.dispose();gpu=null;unavailable=false;}

export class FrameBlurCache {
  private entry:{source:CanvasImageSource;signature:string;canvas:HTMLCanvasElement}|null=null;
  preparations=0;
  get(source:CanvasImageSource,sigmaX:number,sigmaY=sigmaX,revision=''):CanvasImageSource {
    if(sigmaX<=.01&&sigmaY<=.01)return source;
    const width=Number((source as any).width),height=Number((source as any).height);
    const signature=JSON.stringify([width,height,sigmaX,sigmaY,revision]);
    if(this.entry?.source===source&&this.entry.signature===signature)return this.entry.canvas;
    const plan=frameBlurPlan(width,height,sigmaX,sigmaY),input=document.createElement('canvas');
    input.width=plan.width;input.height=plan.height;
    const ctx=input.getContext('2d')!;ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality='high';
    ctx.drawImage(source,0,0,input.width,input.height);
    let output:HTMLCanvasElement;
    try {
      if(unavailable)throw Error('Gaussian GPU unavailable');
      gpu ||= new GaussianRenderer();output=gpu.draw(input,plan.sigmaX,plan.sigmaY);
    }catch{
      gpu?.dispose();gpu=null;unavailable=true;
      output=cpuGaussian(input,plan.sigmaX,plan.sigmaY);
    }
    input.width=input.height=1;
    // Do not resize an old result: a cached camera master can still reference it.
    this.entry={source,signature,canvas:output};this.preparations++;return output;
  }
  clear():void {this.entry=null;}
}

const VERTEX='attribute vec2 position;varying vec2 uv;void main(){uv=position*.5+.5;gl_Position=vec4(position,0.,1.);}';
const FRAGMENT=`precision highp float;
uniform sampler2D image;uniform vec2 direction;uniform float sigma;varying vec2 uv;
void main(){vec4 total=vec4(0.);float weightSum=0.;
 for(int i=-15;i<=15;i++){float x=float(i),weight=exp(-.5*x*x/(sigma*sigma));
 total+=texture2D(image,uv+direction*x)*weight;weightSum+=weight;}
 gl_FragColor=total/weightSum;}`;

class GaussianRenderer {
  private canvas=document.createElement('canvas');
  private gl:WebGLRenderingContext;
  private program:WebGLProgram;
  private buffer:WebGLBuffer;
  private input:WebGLTexture;
  private intermediate:WebGLTexture;
  private framebuffer:WebGLFramebuffer;
  constructor(){
    const gl=this.canvas.getContext('webgl',{alpha:true,premultipliedAlpha:true,antialias:false,depth:false,stencil:false});
    if(!gl)throw Error('No WebGL');this.gl=gl;
    const compile=(type:number,source:string)=>{const s=gl.createShader(type)!;gl.shaderSource(s,source);gl.compileShader(s);return s;};
    const vertex=compile(gl.VERTEX_SHADER,VERTEX),fragment=compile(gl.FRAGMENT_SHADER,FRAGMENT),program=gl.createProgram()!;
    gl.attachShader(program,vertex);gl.attachShader(program,fragment);gl.linkProgram(program);gl.deleteShader(vertex);gl.deleteShader(fragment);
    if(!gl.getProgramParameter(program,gl.LINK_STATUS)){gl.deleteProgram(program);gl.getExtension('WEBGL_lose_context')?.loseContext();throw Error('Gaussian shader failed');}
    this.program=program;this.buffer=gl.createBuffer()!;this.framebuffer=gl.createFramebuffer()!;
    const texture=()=>{const t=gl.createTexture()!;gl.bindTexture(gl.TEXTURE_2D,t);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);return t;};
    this.input=texture();this.intermediate=texture();
    gl.bindBuffer(gl.ARRAY_BUFFER,this.buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,1,1]),gl.STATIC_DRAW);
    gl.useProgram(program);const location=gl.getAttribLocation(program,'position');gl.enableVertexAttribArray(location);gl.vertexAttribPointer(location,2,gl.FLOAT,false,0,0);
  }
  draw(input:HTMLCanvasElement,sigmaX:number,sigmaY:number):HTMLCanvasElement {
    const gl=this.gl,w=input.width,h=input.height;if(gl.isContextLost())throw Error('Gaussian context lost');
    if(this.canvas.width!==w)this.canvas.width=w;if(this.canvas.height!==h)this.canvas.height=h;
    gl.viewport(0,0,w,h);gl.useProgram(this.program);gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D,this.input);gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,1);gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL,1);
    gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,input);
    gl.bindTexture(gl.TEXTURE_2D,this.intermediate);gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,w,h,0,gl.RGBA,gl.UNSIGNED_BYTE,null);
    gl.bindFramebuffer(gl.FRAMEBUFFER,this.framebuffer);gl.framebufferTexture2D(gl.FRAMEBUFFER,gl.COLOR_ATTACHMENT0,gl.TEXTURE_2D,this.intermediate,0);
    if(gl.checkFramebufferStatus(gl.FRAMEBUFFER)!==gl.FRAMEBUFFER_COMPLETE)throw Error('Gaussian framebuffer unavailable');
    const direction=gl.getUniformLocation(this.program,'direction'),sigma=gl.getUniformLocation(this.program,'sigma');
    gl.uniform1i(gl.getUniformLocation(this.program,'image'),0);gl.bindTexture(gl.TEXTURE_2D,this.input);
    gl.uniform2f(direction,1/w,0);gl.uniform1f(sigma,sigmaX);gl.drawArrays(gl.TRIANGLE_STRIP,0,4);
    gl.bindFramebuffer(gl.FRAMEBUFFER,null);gl.bindTexture(gl.TEXTURE_2D,this.intermediate);
    gl.uniform2f(direction,0,1/h);gl.uniform1f(sigma,sigmaY);gl.drawArrays(gl.TRIANGLE_STRIP,0,4);
    const output=document.createElement('canvas');output.width=w;output.height=h;output.getContext('2d')!.drawImage(this.canvas,0,0);return output;
  }
  dispose(){const gl=this.gl;gl.deleteTexture(this.input);gl.deleteTexture(this.intermediate);gl.deleteFramebuffer(this.framebuffer);gl.deleteBuffer(this.buffer);gl.deleteProgram(this.program);gl.getExtension('WEBGL_lose_context')?.loseContext();this.canvas.width=this.canvas.height=1;}
}

/** Deterministic fallback, including browsers without Canvas filter support.
 * Premultiplied channels avoid dark fringes on transparent backgrounds. */
function cpuGaussian(canvas:HTMLCanvasElement,sigmaX:number,sigmaY:number):HTMLCanvasElement {
  const ctx=canvas.getContext('2d')!,w=canvas.width,h=canvas.height,data=ctx.getImageData(0,0,w,h),pixels=new Float32Array(data.data.length);
  for(let i=0;i<pixels.length;i+=4){const a=data.data[i+3]/255;for(let c=0;c<3;c++)pixels[i+c]=data.data[i+c]*a;pixels[i+3]=data.data[i+3];}
  const pass=(input:Float32Array,sigma:number,horizontal:boolean)=>{
    const out=new Float32Array(input.length),radius=Math.min(15,Math.ceil(sigma*3)),weights=[];let sum=0;
    for(let k=-radius;k<=radius;k++){const v=Math.exp(-.5*k*k/(sigma*sigma));weights.push(v);sum+=v;}
    for(let y=0;y<h;y++)for(let x=0;x<w;x++)for(let k=-radius;k<=radius;k++){
      const xx=horizontal?Math.max(0,Math.min(w-1,x+k)):x,yy=horizontal?y:Math.max(0,Math.min(h-1,y+k)),weight=weights[k+radius]/sum;
      for(let c=0;c<4;c++)out[(y*w+x)*4+c]+=input[(yy*w+xx)*4+c]*weight;
    }return out;
  };
  const result=pass(pass(pixels,sigmaX,true),sigmaY,false);
  for(let i=0;i<result.length;i+=4){const a=result[i+3]/255;for(let c=0;c<3;c++)data.data[i+c]=a>0?result[i+c]/a:0;data.data[i+3]=result[i+3];}
  const output=document.createElement('canvas');output.width=w;output.height=h;output.getContext('2d')!.putImageData(data,0,0);return output;
}
